import type { PremierMarineVariantMetadata } from './types';
import { API_NAME_TO_PLAYCANVAS_KEY } from './types';

// ── Helper ──────────────────────────────────────────────────────

/** Build a Record of variants sharing the same attribute-level fields. */
function attr(
  playcanvasPropertyKey: string,
  sectionId: string,
  displayLabel: string,
  variants: [apiName: string, materialName: string, iconUrl: string | null][],
): Record<string, PremierMarineVariantMetadata> {
  return Object.fromEntries(
    variants.map(([apiName, materialName, iconUrl]) => [
      apiName,
      {
        variantKey: `${playcanvasPropertyKey}__${materialName}`,
        playcanvasPropertyKey,
        playcanvasMaterialName: materialName,
        sectionId,
        displayLabel,
        iconUrl,
      } satisfies PremierMarineVariantMetadata,
    ]),
  );
}

// ── Static metadata map ─────────────────────────────────────────
//
// Outer key  = API `proxyName`    (e.g. "Rail Color") [TEST]
// Inner key  = API `variant.name` (e.g. "Black Hammered")
// Value      = PremierMarineVariantMetadata with static fields only
//              (variantId / enabled / layerOrder are merged at runtime)

export const ATTRIBUTE_METADATA_MAP: Record<
  string,
  Record<string, PremierMarineVariantMetadata>
> = {
  // ── Rail Color ────────────────────────────────────────────────
  'Rail Color': attr('railing', 'rail_color__section', 'Rail Color', [
    ['Midnight',       'Midnight',       '/img/Icon/Railing/Midnight.jpg'],
    ['Moonstone',      'Moonstone',      '/img/Icon/Railing/Moonstone.jpg'],
    ['Granite',        'Granite',        '/img/Icon/Railing/Granite.jpg'],
    ['Black Hammered', 'Black_Hammered', '/img/Icon/Railing/Black-Hammered.jpg'],
    ['Sapphire Blue',  'Sapphire_Blue',  '/img/Icon/Railing/Sapphire-Blue.jpg'],
    ['Red',            'Red',            '/img/Icon/Railing/Red.jpg'],
    ['White',          'White',          '/img/Icon/Railing/White.jpg'],
    ['Gunmetal Gray',  'Gunmetal Gray',  '/img/Icon/Railing/Gunmetal-Gray.jpg'],
  ]),

  // ── Bimini Frame ──────────────────────────────────────────────
  'Bimini Frame': attr('biminiFrame', 'playcanvas__biminiFrame', 'Bimini Frame', [
    ['Midnight',      'Midnight',      '/img/Icon/Bimini%20Frame/Midnight.jpg'],
    ['Satine Silver', 'Satine Silver', '/img/Icon/Bimini%20Frame/Satin-Silver.jpg'],
  ]),

  // ── Upper Panel Color ─────────────────────────────────────────
  'Upper Panel Color': attr('upperPanelColor', 'top_panel_color__sections', 'Upper Panel Color', [
    ['Black',         'Black',         '/img/Icon/Panel%20Color/Black.jpg'],
    ['White Pearl',   'White Pearl',   '/img/Icon/Panel%20Color/White-Pearl.jpg'],
    ['Champagne',     'Champagne',     '/img/Icon/Panel%20Color/Champagne.jpg'],
    ['Graphite',      'Graphite',      '/img/Icon/Panel%20Color/Graphite.jpg'],
    ['Nautical Blue', 'Nautical Blue', '/img/Icon/Panel%20Color/Nautical-Blue.jpg'],
    ['Crimson Red',   'Crimson Red',   '/img/Icon/Panel%20Color/Crimson-Red.jpg'],
  ]),

  // ── Lower Panel Color ─────────────────────────────────────────
  'Lower Panel Color': attr('lowerPanelColor', 'bottom_panel_color_section', 'Lower Panel Color', [
    ['Black',         'Black',         '/img/Icon/Panel%20Color/Black.jpg'],
    ['White Pearl',   'White Pearl',   '/img/Icon/Panel%20Color/White-Pearl.jpg'],
    ['Champagne',     'Champagne',     '/img/Icon/Panel%20Color/Champagne.jpg'],
    ['Graphite',      'Graphite',      '/img/Icon/Panel%20Color/Graphite.jpg'],
    ['Nautical Blue', 'Nautical Blue', '/img/Icon/Panel%20Color/Nautical-Blue.jpg'],
    ['Crimson Red',   'Crimson Red',   '/img/Icon/Panel%20Color/Crimson-Red.jpg'],
  ]),

  // ── Base Color (furnitureBaseColor) ────────────────────────────
  // ⚠️  Sunsation & Solaris use "_main" suffix in PlayCanvas material names.
  //     Intrigue & Escalante do NOT use the suffix.
  //     Note: PlayCanvas spells "Dessert" (double S) and "Grey" (not "Gray").
  'Base Color': attr('furnitureBaseColor', 'base_color_selection', 'Base Color', [
    // sunsation  (PlayCanvas uses _main suffix + own spelling)
    ['Desert Sand',         'Dessert_Sand_main',          '/img/Icon/Furniture%20Base%20Color/Desert-Sand-Main.jpg'],
    ['Nightfall Cool Touch','Nightfall_Cool_Touch_main',   '/img/Icon/Furniture%20Base%20Color/Nightfall-Cool-Touch-Main.jpg'],
    ['Firewood Cool Touch', 'Firewood_Cool_Touch_main',    '/img/Icon/Furniture%20Base%20Color/Firewood-Cool-Touch-Main.jpg'],
    ['Mountain Gray',       'Mountain_Grey_main',          '/img/Icon/Furniture%20Base%20Color/Mountain-Gray-Main.jpg'],
    // solaris  (PlayCanvas uses _main suffix)
    ['Sandbar',   'Sandbar_main',   '/img/Icon/Furniture%20Base%20Color/Sandbar-Main.jpg'],
    ['Firewood',  'Firewood_main',  '/img/Icon/Furniture%20Base%20Color/Firewood-Main.jpg'],
    ['Overcast',  'Overcast_main',  '/img/Icon/Furniture%20Base%20Color/Overcast-Main.jpg'],
    ['Nightfall', 'Nightfall_main', '/img/Icon/Furniture%20Base%20Color/Nightfall-Main.jpg'],
    // intrigue  (no suffix)
    ['White Cap',  'White Cap',  '/img/Icon/Furniture%20Base%20Color/White-Cap.jpg'],
    ['Dark Mist',  'Dark Mist',  '/img/Icon/ACCENT%20COLORS/Dark-Mist.jpg'],
    ['Light Mist', 'Light Mist', '/img/Icon/ACCENT%20COLORS/Light-Mist.jpg'],
    ['Midnight',   'Midnight',   '/img/Icon/Furniture%20Base%20Color/Midnight.jpg'],
    ['Terra Clay', 'Terra Clay', '/img/Icon/Furniture%20Base%20Color/Terra-Clay.jpg'],
    // escalante  (no suffix)
    ['Stone',  'Stone',  '/img/Icon/Furniture%20Base%20Color/Stone.jpg'],
    ['Cream',  'Cream',  '/img/Icon/Furniture%20Base%20Color/Cream.jpg'],
    ['Slate',  'Slate',  '/img/Icon/Furniture%20Base%20Color/Slate.jpg'],
    ['Toffee', 'Toffee', '/img/Icon/Furniture%20Base%20Color/Toffee.jpg'],
    ['Cognac', 'Cognac', '/img/Icon/Furniture%20Base%20Color/Cognac.jpg'],
    ['Blonde', 'Blonde', '/img/Icon/Furniture%20Base%20Color/Blonde.jpg'],
  ]),

  // ── Accent Color (furnitureAccentColor) ────────────────────────
  // ⚠️  PlayCanvas runtime values (from getAvailableOptions) use underscores
  //     for some multi-word names, while the Vivid API uses spaces.
  //     The second column (playcanvasMaterialName) MUST match PlayCanvas exactly.
  'Accent Color': attr('furnitureAccentColor', 'accent_color_selection', 'Accent Color', [
    // sunsation  (verified from PlayCanvas runtime)
    ['Hazel Tweed',           'Hazel Tweed',             '/img/Icon/ACCENT%20COLORS/Hazel-Tweed.jpg'],
    ['Bronze Weave',          'Bronze_Weave',            '/img/Icon/ACCENT%20COLORS/Bronze-Weave.jpg'],
    ['Arctic',                'Arctic',                  '/img/Icon/ACCENT%20COLORS/Arctic.jpg'],
    ['Ash Tweed',             'Ash Tweed',               '/img/Icon/ACCENT%20COLORS/Ash-Tweed.jpg'],
    ['Carbon Weave',          'Carbon_Weave',            '/img/Icon/ACCENT%20COLORS/Carbon-Weave.jpg'],
    ['Nautical Blue Diamond', 'Nautical_Blue_Diamond',   '/img/Icon/ACCENT%20COLORS/Nautical-Blue-Diamond.jpg'],
    ['Red Diamond',           'Red_Diamond',             '/img/Icon/ACCENT%20COLORS/Crimson-Red-Diamond.jpg'],
    // solaris
    ['Arctic Diamond',  'Arctic_Diamond',  '/img/Icon/ACCENT%20COLORS/Arctic-Diamond.jpg'],
    ['Twilight Twill',  'Twilight_Twill',  '/img/Icon/ACCENT%20COLORS/Twilight-Twill.jpg'],
    ['Quilted',         'Quilted',         '/img/Icon/ACCENT%20COLORS/Quilted.jpg'],
    // escalante
    ['Stone',  'Stone',  '/img/Icon/ACCENT%20COLORS/Stone.jpg'],
    ['Cream',  'Cream',  '/img/Icon/ACCENT%20COLORS/Cream.jpg'],
    ['Slate',  'Slate',  '/img/Icon/ACCENT%20COLORS/Slate.jpg'],
    ['Toffee', 'Toffee', '/img/Icon/ACCENT%20COLORS/Toffee.jpg'],
    ['Cognac', 'Cognac', '/img/Icon/ACCENT%20COLORS/Cognac.jpg'],
    ['Blonde', 'Blonde', '/img/Icon/ACCENT%20COLORS/Blonde.jpg'],
  ]),

  // ── Accent Color 2 — intrigue only (accentColor) ────────────────
  // PlayCanvas uses a DIFFERENT property key for intrigue accent colors:
  //   furnitureAccentColor → sunsation, solaris, escalante
  //   accentColor          → intrigue ONLY
  // The Vivid API returns both as "Accent Color", so buildInitialAttributesFromAPI
  // must pick the correct key based on boatType.
  'Accent Color (intrigue)': attr('accentColor', 'accent_color_2_selection', 'Accent Color', [
    ['Pearl Loop',     'Pearl Loop',     '/img/Icon/ACCENT%20COLORS/Pearl-Loop.jpg'],
    ['Twilight Twill', 'Twilight_Twill', '/img/Icon/ACCENT%20COLORS/Twilight-Twill.jpg'],
    ['White Cap',      'White Cap',      '/img/Icon/ACCENT%20COLORS/White-Cap.jpg'],
    ['Dark Mist',      'Dark Mist',      '/img/Icon/ACCENT%20COLORS/Dark-Mist.jpg'],
    ['Light Mist',     'Light Mist',     '/img/Icon/ACCENT%20COLORS/Light-Mist.jpg'],
    ['Midnight',       'Midnight',       '/img/Icon/ACCENT%20COLORS/Midnight.jpg'],
    ['Terra Clay',     'Terra Clay',     '/img/Icon/ACCENT%20COLORS/Terra-Clay.jpg'],
  ]),

  // ── Fiberglass ────────────────────────────────────────────────
  'Fiberglass': attr('fiberglass', 'fiberglass_color__section', 'Fiberglass', [
    ['Desert Sand',   'Desert_Sand',   '/img/Icon/FIBERGLASS/Desert-Sand.jpg'],
    ['Mountain Grey', 'Mountain_Grey', '/img/Icon/FIBERGLASS/Mountain-Gray.jpg'],
    ['Black',         'Black',         '/img/Icon/FIBERGLASS/Black.jpg'],
    ['Gunmetal Gray', 'Gunmetal Gray', '/img/Icon/FIBERGLASS/Gunmetal-Gray.jpg'],
    ['White',         'White',         '/img/Icon/FIBERGLASS/White.jpg'],
  ]),

  // ── Canvas Color ──────────────────────────────────────────────
  'Canvas Color': attr('canvas', 'cover_color__section', 'Canvas Color', [
    ['Harbor Gray',   'Harbor_Gray',   '/img/Icon/CANVAS/Harbor-Gray.jpg'],
    ['Nautical Blue', 'Nautical_Blue', '/img/Icon/CANVAS/Nautical-Blue.jpg'],
    ['Onyx',          'Onyx',          '/img/Icon/CANVAS/Onyx.jpg'],
  ]),

  // ── Flooring Color ────────────────────────────────────────────
  'Flooring Color': attr('flooring', 'flooring_color__sections', 'Flooring Color', [
    ['Sterling Shores',    'Sterling_Shores',    '/img/Icon/FLOORING/Sterling-Shores.jpg'],
    ['Slate Teak',         'Slate_Teak',         '/img/Icon/FLOORING/Slate-Teak.jpg'],
    ['Mahogany Teak',      'Mahogany_Teak',      '/img/Icon/FLOORING/Mahogany-Teak.jpg'],
    ['Driftwood',          'Driftwood',           '/img/Icon/FLOORING/Driftwood.jpg'],
    ['Double Desert Teak', 'Double_Desert_Teak', '/img/Icon/FLOORING/Double-Desert-Teak.jpg'],
    ['Birchwood',          'Birchwood',           '/img/Icon/FLOORING/Birchwood.jpg'],
  ]),
};

// ── Lookup function ─────────────────────────────────────────────

/** Default metadata returned when a variant is not found in the static map. */
function makeDefault(
  attrName: string,
  variantName: string,
): PremierMarineVariantMetadata {
  const playcanvasPropertyKey =
    API_NAME_TO_PLAYCANVAS_KEY[attrName] ?? attrName;

  return {
    variantKey: `${playcanvasPropertyKey}__${variantName}`,
    playcanvasPropertyKey,
    playcanvasMaterialName: variantName,
    sectionId: `playcanvas__${playcanvasPropertyKey}`,
    displayLabel: attrName,
    iconUrl: null,
  };
}

/**
 * Look up project-specific metadata for a single variant.
 *
 * @param attrName    — API attribute name (e.g. `"Rail Color"`)
 * @param variantName — API variant name   (e.g. `"Black Hammered"`)
 * @returns metadata from the static map, or a sensible default if not found.
 *
 * @example
 * ```ts
 * const meta = getPremierMarineVariantMetadata('Rail Color', 'Black Hammered');
 * // meta.playcanvasMaterialName === 'Black_Hammered'
 * // meta.iconUrl === '/img/Icon/Railing/Black-Hammered.jpg'
 * ```
 */
export function getPremierMarineVariantMetadata(
  attrName: string,
  variantName: string,
): PremierMarineVariantMetadata {
  return (
    ATTRIBUTE_METADATA_MAP[attrName]?.[variantName] ??
    makeDefault(attrName, variantName)
  );
}
